import { useState, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import { toast as sonnerToast } from "sonner";

export interface SocialIndexEvents {
  startedEvent: string;
  completedEvent: string;
}

export interface SocialIndexConfig {
  triggerEndpoint: string;
  statusEndpoint: string;
  events: SocialIndexEvents;
  successStatuses?: string[];
  pollIntervalMs?: number;
  timeoutMs?: number;
  safetyToastMs?: number;
  startToast?: string;
  completeToast?: string;
  redirectToChatOnSuccess?: boolean;
  indexingMessages: string[];
}

export interface UseSocialIndexResult {
  isIndexing: Record<string, boolean>;
  currentMessageIndex: Record<string, number>;
  indexingMessages: string[];
  triggerIndex: (accountId: string, connectionId: string, skipPost?: boolean) => Promise<void>;
}

export function useSocialIndex(config: SocialIndexConfig): UseSocialIndexResult {
  const { user } = useAuth();
  const router = useRouter();
  const [isIndexing, setIsIndexing] = useState<Record<string, boolean>>({});
  const [currentMessageIndex, setCurrentMessageIndex] = useState<Record<string, number>>({});
  const [messageTimerId, setMessageTimerId] = useState<Record<string, any>>({});
  const [mainTimerId, setMainTimerId] = useState<Record<string, any>>({});
  const indexingStartedRef = useRef<Record<string, boolean>>({});

  const successSet = new Set(
    config.successStatuses ?? ["success", "partial_success", "succeeded", "completed", "done"]
  );
  const pollIntervalMs = config.pollIntervalMs ?? 10000;
  const timeoutMs = config.timeoutMs ?? 10 * 60 * 1000;
  const safetyToastMs = config.safetyToastMs ?? 120000;

  const triggerIndex = useCallback(
    async (accountId: string, connectionId: string, skipPost = false) => {
      try {
        console.log("[SOCIAL INDEX] triggerIndex called", {
          triggerEndpoint: config.triggerEndpoint,
          accountId,
          connectionId,
          skipPost,
          indexingStartedRef: indexingStartedRef.current[accountId],
          isIndexing: isIndexing[accountId],
        });
      } catch {}

      if (indexingStartedRef.current[accountId] || isIndexing[accountId]) {
        try {
          console.log("[SOCIAL INDEX] Trigger blocked by guard", {
            triggerEndpoint: config.triggerEndpoint,
            accountId,
            connectionId,
            indexingStartedRef: indexingStartedRef.current[accountId],
            isIndexing: isIndexing[accountId],
          });
        } catch {}
        return;
      }
      indexingStartedRef.current[accountId] = true;

      try {
        // Runtime debug logging for social index triggers (e.g. YouTube "Update Lema data")
        console.log("[SOCIAL INDEX] Trigger requested", {
          triggerEndpoint: config.triggerEndpoint,
          statusEndpoint: config.statusEndpoint,
          accountId,
          connectionId,
        });
      } catch {}

      const existingMsg = messageTimerId[accountId];
      if (existingMsg) {
        try { clearInterval(existingMsg as any); } catch {}
      }
      const existingMain = mainTimerId[accountId];
      if (existingMain) {
        try { clearTimeout(existingMain as any); } catch {}
      }

      setIsIndexing(prev => ({ ...prev, [accountId]: true }));
      setCurrentMessageIndex(prev => ({ ...prev, [accountId]: 0 }));

      try {
        window.dispatchEvent(new CustomEvent(config.events.startedEvent));
      } catch {}

      const msgInterval = setInterval(() => {
        setCurrentMessageIndex(prev => {
          const cur = prev[accountId] || 0;
          const next = (cur + 1) % config.indexingMessages.length;
          return { ...prev, [accountId]: next };
        });
      }, 3000);
      setMessageTimerId(prev => ({ ...prev, [accountId]: msgInterval as any }));

      const safetyTimer = setTimeout(() => {
        sonnerToast.info(
          "Indexing is taking longer than expected. We'll keep working in the background."
        );
      }, safetyToastMs);
      setMainTimerId(prev => ({ ...prev, [accountId]: safetyTimer as any }));

      try {
        if (!skipPost) {
          console.log("[SOCIAL INDEX] Making fetch request", {
            endpoint: config.triggerEndpoint,
            method: "POST",
            connectionId,
          });

          const triggerResp = await fetch(config.triggerEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ connectionId }),
          });

          console.log("[SOCIAL INDEX] Fetch response received", {
            endpoint: config.triggerEndpoint,
            status: triggerResp.status,
            ok: triggerResp.ok,
            statusText: triggerResp.statusText,
          });

          if (!triggerResp.ok) {
            // Auth or other error starting indexing; stop early and surface error.
            let backendError: any = null;
            try {
              backendError = await triggerResp.json();
            } catch {}

            try {
              console.error("[SOCIAL INDEX] Trigger failed", {
                triggerEndpoint: config.triggerEndpoint,
                status: triggerResp.status,
                statusText: triggerResp.statusText,
                backendError,
                accountId,
                connectionId,
              });
            } catch {}

            // Check if this is a session expired error
            if (
              triggerResp.status === 401 &&
              (backendError?.code === 'session_expired' ||
               backendError?.error?.toLowerCase().includes('session expired') ||
               backendError?.error?.toLowerCase().includes('please sign in'))
            ) {
              // Session expired - trigger re-authentication
              sonnerToast.error('Your session has expired. Please sign in again.');

              // Sign out to clear invalid session and redirect to login
              try {
                // Use setTimeout to avoid blocking the error flow
                setTimeout(() => {
                  window.location.href = '/?session_expired=true';
                }, 1000);
              } catch {}

              throw new Error('Session expired. Please sign in again.');
            }

            const backendMessage =
              (backendError && (backendError.error || backendError.detail)) ||
              `Failed to start indexing (status ${triggerResp.status})`;

            throw new Error(String(backendMessage));
          }

          if (config.startToast) {
            sonnerToast.success(config.startToast);
          }
        }

        const start = Date.now();

        while (Date.now() - start < timeoutMs) {
          try {
            const qs = new URLSearchParams({ connectionId });
            const resp = await fetch(`${config.statusEndpoint}?${qs.toString()}`, {
              cache: "no-store",
            });
            const json = await resp.json();
            const status = (json?.status || "").toString().toLowerCase();

            if (successSet.has(status)) {
              try { clearTimeout(safetyTimer as any); } catch {}
              try { clearInterval(msgInterval as any); } catch {}

              setIsIndexing(prev => ({ ...prev, [accountId]: false }));
              setMessageTimerId(prev => ({ ...prev, [accountId]: null }));
              setMainTimerId(prev => ({ ...prev, [accountId]: null }));
              indexingStartedRef.current[accountId] = false;

              try {
                window.dispatchEvent(
                  new CustomEvent(config.events.completedEvent, { detail: { status } })
                );
              } catch {}

              if (config.completeToast) {
                sonnerToast.success(config.completeToast);
              }

              if (config.redirectToChatOnSuccess) {
                if (user?.id) {
                  router.push(`/chat/${user.id}`);
                } else {
                  router.push("/chat");
                }
              }
              return;
            }
          } catch {}

          await new Promise(r => setTimeout(r, pollIntervalMs));
        }

        try { clearTimeout(safetyTimer as any); } catch {}
        try { clearInterval(msgInterval as any); } catch {}
        setIsIndexing(prev => ({ ...prev, [accountId]: false }));
        setMessageTimerId(prev => ({ ...prev, [accountId]: null }));
        setMainTimerId(prev => ({ ...prev, [accountId]: null }));
        indexingStartedRef.current[accountId] = false;

        try {
          window.dispatchEvent(
            new CustomEvent(config.events.completedEvent, { detail: { status: "error" } })
          );
        } catch {}
      } catch (error: any) {
        try {
          console.error("[SOCIAL INDEX] triggerIndex error", {
            triggerEndpoint: config.triggerEndpoint,
            statusEndpoint: config.statusEndpoint,
            accountId,
            connectionId,
            errorMessage: error?.message ?? String(error),
          });
        } catch {}

        try { clearTimeout(safetyTimer as any); } catch {}
        try { clearInterval(msgInterval as any); } catch {}
        setIsIndexing(prev => ({ ...prev, [accountId]: false }));
        setMessageTimerId(prev => ({ ...prev, [accountId]: null }));
        setMainTimerId(prev => ({ ...prev, [accountId]: null }));
        indexingStartedRef.current[accountId] = false;

        try {
          window.dispatchEvent(
            new CustomEvent(config.events.completedEvent, { detail: { status: "error" } })
          );
        } catch {}

        sonnerToast.error(error?.message || "Failed to start indexing");
      }
    }, [config, isIndexing, mainTimerId, messageTimerId, pollIntervalMs, safetyToastMs, successSet, timeoutMs, router, user]);

  return {
    isIndexing,
    currentMessageIndex,
    indexingMessages: config.indexingMessages,
    triggerIndex,
  };
}
